import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    customerInfoProviderToken,
    globalLoggerToken,
    gwStorefrontAppInfoToken,
    localStorageToken,
    modalManagerToken,
} from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import MinilogLogger from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import DrawerManager from "@components/Drawer/DrawerManager";
import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import { PRODUCT_HANDLE_STORAGE_KEY } from "./constants";
import {
    AddToRegistryModalWidgetDefinition,
    AddToRegistryProductPageWidgetDefinition,
    GiftCartDrawerWidgetDefinition,
    GiftRegistryPageWidgetDefinition,
    RegistryFormModalWidgetDefinition,
    SharedRegistryPageWidgetDefinition,
} from "./definitions";
import {
    GiftRegistryApplicationConfigInterface,
    GiftRegistryApplicationConfigSchema,
    GiftRegistryApplicationConfigToken,
} from "./GiftRegistryApplicationConfig";
import {
    giftCartDrawerManagerToken,
    giftRegistryApplicationLoggerToken,
    giftRegistryAuthApiClientToken,
} from "./tokens";
import AddToRegistryModalWidgetInjector from "./widgets/add_to_registry_modal_widget/AddToRegistryModalWidgetInjector";
import AddToRegistryProductPageWidgetInjector from "./widgets/add_to_registry_product_page_widget/AddToRegistryProductPageWidgetInjector";
import GiftCartDrawerWidgetInjector from "./widgets/gift_cart_drawer_widget/GiftCartDrawerWidgetInjector";
import RegistryFormModalWidgetInjector from "./widgets/registry_form_modal_widget/RegistryFormModalWidgetInjector";
import SharedRegistryPageWidgetInjector from "./widgets/shared_registry_page_widget/SharedRegistryPageWidgetInjector";

@singleton()
export class GiftRegistryApplication implements ApplicationInterface<object> {
    widgets = [];
    exports = {};

    constructor(
        @inject(GiftRegistryApplicationConfigToken)
        public readonly config: GiftRegistryApplicationConfigInterface,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule
    ) {
        assert(config, GiftRegistryApplicationConfigSchema);

        const dynamicImportManager =
            dynamicImportModule.exports.dynamicImportManager;

        dynamicImportManager.register(AddToRegistryModalWidgetDefinition);
        dynamicImportManager.register(AddToRegistryProductPageWidgetDefinition);
        dynamicImportManager.register(GiftCartDrawerWidgetDefinition);
        dynamicImportManager.register(RegistryFormModalWidgetDefinition);
        dynamicImportManager.register(SharedRegistryPageWidgetDefinition);
        dynamicImportManager.register(GiftRegistryPageWidgetDefinition);
    }

    init(): this {
        const logger = new MinilogLogger(App.GiftRegistry);
        const locationController = this.commonModule.exports.locationController;
        const urlsBuilder = this.commonModule.exports.urlsBuilder;
        const modalManager = container.resolve(modalManagerToken);
        const customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );

        const customerId = customerInfoProvider.getId();
        const localStorage = container.resolve(localStorageToken);
        const productHandle = localStorage.getItem(PRODUCT_HANDLE_STORAGE_KEY);

        if (customerId && productHandle) {
            localStorage.removeItem(PRODUCT_HANDLE_STORAGE_KEY);
            locationController.goTo(
                urlsBuilder.createProductUrl(productHandle)
            );
        }

        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );
        const eventsCollector =
            analyticEventsCollectorModule.exports.eventsCollector;
        const analyticsEventsFactory =
            analyticEventsCollectorModule.exports.eventsFactory;

        const giftCartDrawerManager = new DrawerManager();

        container.registerInstance(
            giftCartDrawerManagerToken,
            giftCartDrawerManager
        );

        container.registerInstance(giftRegistryApplicationLoggerToken, logger);
        container.register(giftRegistryAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/v1/gift-registries-service"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        const addToRegistryProductPageWidgetInjector =
            new AddToRegistryProductPageWidgetInjector(
                this.config.addToRegistryProductPageWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager
            );

        const addToRegistryModalWidgetInjector =
            new AddToRegistryModalWidgetInjector(
                this.config.addToRegistryModalWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager
            );

        const sharedRegistryPageWidgetInjector =
            new SharedRegistryPageWidgetInjector(
                this.config.sharedRegistryPageWidgetPlaceholder,
                logger,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager
            );

        const registryFormModalWidgetInjector =
            new RegistryFormModalWidgetInjector(
                this.config.registryFormModalWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager
            );

        const giftCartDrawerWidgetInjector = new GiftCartDrawerWidgetInjector(
            this.config.giftCartDrawerWidgetPlaceholder,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager
        );

        addToRegistryProductPageWidgetInjector.start();
        addToRegistryModalWidgetInjector.start();
        sharedRegistryPageWidgetInjector.start();
        registryFormModalWidgetInjector.start();
        giftCartDrawerWidgetInjector.start();

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.GiftRegistry,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}
